type Spark = {
  x: number;
  y: number;
  angle: number;
  speed: number;
  delay: number;
  life: number;
  size: number;
  color: string;
};

export default function setupCommunityFireworks(section: HTMLElement, reducedMotion: MediaQueryList) {
  const scene = section.parentElement;
  const setUnveil = (progress: number) => scene?.style.setProperty('--s6-unveil', progress.toFixed(3));
  const canvas = section.querySelector<HTMLCanvasElement>('.section-6__fireworks');
  const ctx = canvas?.getContext('2d');
  if (!canvas || !ctx) {
    setUnveil(1);
    return { start: () => {}, reset: () => {}, destroy: () => scene?.style.removeProperty('--s6-unveil') };
  }
  const colors = ['255,95,159', '154,225,255', '255,222,151', '246,249,255'];
  const burstAt = 0.78;
  let width = 0;
  let height = 0;
  let frameId = 0;
  let startedAt = 0;
  let played = false;
  let sparks: Spark[] = [];

  section.classList.add('is-firework-ready');
  setUnveil(0);

  const fitCanvas = () => {
    width = window.innerWidth;
    height = window.innerHeight;
    const dpr = Math.min(window.devicePixelRatio || 1, 2);
    canvas.width = Math.round(width * dpr);
    canvas.height = Math.round(height * dpr);
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
  };

  const seedSparks = () => {
    const spread = Math.min(width, height) * 0.43;
    const cx = width * 0.5;
    const cy = height * 0.34;
    sparks = [];
    const addBurst = (count: number, x: number, y: number, delay: number, speed: number) => {
      for (let i = 0; i < count; i += 1) {
        sparks.push({
          x,
          y,
          angle: (i / count) * Math.PI * 2 + (Math.random() - 0.5) * 0.16,
          speed: spread * speed * (0.75 + Math.random() * 0.5),
          delay: delay + Math.random() * 0.08,
          life: 1.05 + Math.random() * 0.45,
          size: 1.4 + Math.random() * 1.5,
          color: colors[Math.floor(Math.random() * colors.length)],
        });
      }
    };
    addBurst(width < 600 ? 60 : 80, cx, cy, 0, 1.35);
    addBurst(width < 600 ? 18 : 24, cx, cy, 0.09, 0.48);
    addBurst(width < 600 ? 18 : 22, cx - spread * 0.38, cy + spread * 0.12, 0.2, 0.72);
    addBurst(width < 600 ? 18 : 22, cx + spread * 0.42, cy - spread * 0.07, 0.29, 0.76);
  };

  const rocketPoint = (progress: number) => ({
    x: width * (0.5 - Math.sin(progress * Math.PI) * 0.035),
    y: height + 24 - (height * 0.66 + 24) * (1 - Math.pow(1 - progress, 1.65)),
  });

  const drawRocket = (elapsed: number) => {
    const progress = Math.min(1, Math.max(0, (elapsed - 0.12) / (burstAt - 0.12)));
    if (progress <= 0 || progress >= 1) { return; }
    const tip = rocketPoint(progress);
    const trail = ctx.createLinearGradient(width * 0.5, height, tip.x, tip.y);
    trail.addColorStop(0, 'rgba(255,64,129,0)');
    trail.addColorStop(0.58, 'rgba(255,64,129,0.42)');
    trail.addColorStop(0.88, 'rgba(154,225,255,0.9)');
    trail.addColorStop(1, '#fff8df');
    ctx.beginPath();
    for (let i = 0; i <= 28; i += 1) {
      const point = rocketPoint(progress * i / 28);
      if (i === 0) { ctx.moveTo(point.x, point.y); } else { ctx.lineTo(point.x, point.y); }
    }
    ctx.lineCap = 'round';
    ctx.strokeStyle = trail;
    ctx.shadowColor = '#ff7eb8';
    ctx.shadowBlur = 20;
    ctx.lineWidth = 3.4;
    ctx.stroke();
    ctx.shadowBlur = 0;

    for (let i = 1; i <= 12; i += 1) {
      const point = rocketPoint(Math.max(0, progress - i * 0.025));
      ctx.fillStyle = `rgba(255,225,185,${(1 - i / 13) * 0.75})`;
      ctx.beginPath();
      ctx.arc(point.x + Math.sin(i * 7) * i * 0.8, point.y, 1.6 - i * 0.07, 0, Math.PI * 2);
      ctx.fill();
    }
    const glow = ctx.createRadialGradient(tip.x, tip.y, 0, tip.x, tip.y, 27);
    glow.addColorStop(0, '#ffffff');
    glow.addColorStop(0.18, '#ffe6b3');
    glow.addColorStop(0.48, 'rgba(255,95,159,0.45)');
    glow.addColorStop(1, 'rgba(255,95,159,0)');
    ctx.fillStyle = glow;
    ctx.beginPath();
    ctx.arc(tip.x, tip.y, 27, 0, Math.PI * 2);
    ctx.fill();
  };

  const drawBurst = (elapsed: number) => {
    const age = elapsed - burstAt;
    if (age < 0) { return; }
    const cx = width * 0.5;
    const cy = height * 0.34;
    if (age < 0.42) {
      const fade = 1 - age / 0.42;
      const glow = ctx.createRadialGradient(cx, cy, 0, cx, cy, 110 * (1 - fade) + 28);
      glow.addColorStop(0, `rgba(255,255,255,${fade * 0.95})`);
      glow.addColorStop(0.28, `rgba(255,180,215,${fade * 0.48})`);
      glow.addColorStop(1, 'rgba(255,180,215,0)');
      ctx.fillStyle = glow;
      ctx.beginPath();
      ctx.arc(cx, cy, 110 * (1 - fade) + 28, 0, Math.PI * 2);
      ctx.fill();
      ctx.beginPath();
      ctx.arc(cx, cy, 18 + age * 220, 0, Math.PI * 2);
      ctx.strokeStyle = `rgba(255,235,247,${fade * 0.52})`;
      ctx.lineWidth = 2.2;
      ctx.stroke();
    }

    ctx.lineCap = 'round';
    for (const spark of sparks) {
      const sparkAge = age - spark.delay;
      if (sparkAge < 0 || sparkAge > spark.life) { continue; }
      const progress = sparkAge / spark.life;
      const distance = spark.speed * sparkAge * (1 - progress * 0.18);
      const dx = Math.cos(spark.angle);
      const dy = Math.sin(spark.angle);
      const x = spark.x + dx * distance;
      const y = spark.y + dy * distance + 60 * sparkAge * sparkAge;
      const alpha = Math.pow(1 - progress, 1.25) * (0.78 + 0.22 * Math.sin(sparkAge * 34));
      const tail = (14 + spark.size * 9) * (1 - progress * 0.55);
      ctx.strokeStyle = `rgba(${spark.color},${alpha})`;
      ctx.fillStyle = ctx.strokeStyle;
      ctx.lineWidth = spark.size * (1 - progress * 0.55);
      ctx.beginPath();
      ctx.moveTo(x - dx * tail, y - dy * tail);
      ctx.lineTo(x, y);
      ctx.stroke();
      ctx.beginPath();
      ctx.arc(x, y, Math.max(0.5, spark.size * 0.7 * (1 - progress)), 0, Math.PI * 2);
      ctx.fill();
      ctx.fillStyle = `rgba(${spark.color},${alpha * 0.42})`;
      ctx.beginPath();
      ctx.arc(x - dx * tail * 1.55 + Math.sin(spark.angle * 7) * 3, y - dy * tail * 1.55, spark.size * 0.42, 0, Math.PI * 2);
      ctx.moveTo(x - dx * tail * 2.25 - Math.cos(spark.angle * 9) * 4 + spark.size * 0.26, y - dy * tail * 2.25);
      ctx.arc(x - dx * tail * 2.25 - Math.cos(spark.angle * 9) * 4, y - dy * tail * 2.25, spark.size * 0.26, 0, Math.PI * 2);
      ctx.fill();
    }
  };

  const render = (now: number) => {
    const elapsed = (now - startedAt) / 1000;
    const reveal = Math.min(1, Math.max(0, (elapsed - burstAt - 0.18) / 1.12));
    const unveil = Math.pow(reveal, 1.6);
    if (reveal > 0) { setUnveil(unveil); }
    ctx.clearRect(0, 0, width, height);
    if (elapsed < 1.9) {
      ctx.fillStyle = `rgba(9,18,35,${0.93 * Math.min(1, elapsed / 0.26)})`;
      ctx.fillRect(0, 0, width, height);
      if (reveal > 0) {
        const radius = Math.hypot(width, height) * unveil;
        const fade = ctx.createRadialGradient(width * 0.5, height * 0.34, Math.max(0, radius - 170), width * 0.5, height * 0.34, Math.max(1, radius));
        fade.addColorStop(0, 'rgba(0,0,0,1)');
        fade.addColorStop(1, 'rgba(0,0,0,0)');
        ctx.globalCompositeOperation = 'destination-out';
        ctx.fillStyle = fade;
        ctx.fillRect(0, 0, width, height);
        ctx.globalCompositeOperation = 'source-over';
      }
    }
    drawRocket(elapsed);
    drawBurst(elapsed);
    if (elapsed >= burstAt + 0.42) { section.classList.add('is-revealed'); }
    if (elapsed < 2.5) {
      frameId = requestAnimationFrame(render);
    } else {
      frameId = 0;
      section.classList.remove('is-celebrating');
      ctx.clearRect(0, 0, width, height);
    }
  };

  const start = () => {
    if (played) { return; }
    played = true;
    if (reducedMotion.matches) {
      setUnveil(1);
      section.classList.add('is-revealed');
      return;
    }
    fitCanvas();
    seedSparks();
    section.classList.add('is-celebrating');
    startedAt = performance.now();
    frameId = requestAnimationFrame(render);
  };

  const reset = () => {
    if (!played) { return; }
    played = false;
    if (frameId) { cancelAnimationFrame(frameId); }
    frameId = 0;
    setUnveil(0);
    ctx.clearRect(0, 0, width, height);
    section.classList.remove('is-celebrating', 'is-revealed');
  };

  const onResize = () => { if (frameId) { fitCanvas(); } };
  const onMotionChange = () => {
    if (!reducedMotion.matches || !frameId) { return; }
    cancelAnimationFrame(frameId);
    frameId = 0;
    setUnveil(1);
    ctx.clearRect(0, 0, width, height);
    section.classList.remove('is-celebrating');
    section.classList.add('is-revealed');
  };
  window.addEventListener('resize', onResize);
  reducedMotion.addEventListener('change', onMotionChange);

  return {
    start,
    reset,
    destroy: () => {
      reset();
      window.removeEventListener('resize', onResize);
      reducedMotion.removeEventListener('change', onMotionChange);
      section.classList.remove('is-firework-ready');
      scene?.style.removeProperty('--s6-unveil');
    },
  };
}
